<template>
  <Modal
    full-height
    :position="ModalPosition.RIGHT"
    :shape="ModalShape.SQUARE"
    :size="ModalSize.SM"
  >
    <template #activator="{ activatorProps }">
      <Btn
        v-bind="activatorProps"
        icon
        :size="BtnSize.LG"
        :color="BtnColor.TERTIARY"
        :variant="BtnVariant.OUTLINED"
        aria-label="Open filters modal"
      >
        <Icon :icon="FilterSvg" :size="IconSize.SM" />
      </Btn>
    </template>

    <template #header="{ closeModal }">
      <div class="pt-8"></div>
      <FilterHeader :title="modalTitle" @back="onFilterCancel(closeModal)" />
      <ActiveFilters
        v-if="currentTab === FilterTab.GENERAL"
        :filters="filtersToApply"
        :current-category="currentCategory"
        :current-subcategory="currentSubcategory"
        :type-options="typeOptions"
        :tags="props.hub.tags"
        @close-category="filtersToApply.categoryId = null"
        @close-subcategory="filtersToApply.subcategoryId = null"
        @close-type="onTypeRemove($event)"
        @close-tag="onTagRemove($event)"
        @close-status="onStatusRemove($event)"
      />
      <Btn
        v-if="currentTab === FilterTab.GENERAL && isFilterApplied"
        class="self-end"
        :size="BtnSize.LG"
        :color="BtnColor.PRIMARY"
        @click="onFiltersReset"
      >
        {{ $t('filters-modal.reset-filters') }}
      </Btn>
    </template>

    <template #body>
      <div v-if="currentTab === FilterTab.GENERAL" class="flex flex-col">
        <div class="mt-2">
          <h3 class="px-4 font-bold text-secondary">Categories</h3>
          <FilterBtn
            class="mt-2"
            :model-value="filtersToApply.categoryId === null"
            with-background
            with-checkbox
            @update:model-value="setDefaultCategory"
          >
            {{ $t('filters-modal.all') }}
          </FilterBtn>
          <FilterBtn
            v-for="(category, index) in props.hub.categories"
            :key="`category-${index}`"
            :model-value="category.id === filtersToApply.categoryId"
            :with-background="index % 2 !== 0"
            :with-button="category.subcategories.length > 0"
            with-checkbox
            @choose-filter="setCategory(category, true)"
            @update:model-value="setCategory(category)"
          >
            {{ category.name }}
          </FilterBtn>
        </div>

        <div class="mt-7">
          <h3 class="px-4 font-bold text-secondary">Filters</h3>
          <FilterBtn
            class="mt-2"
            with-background
            with-button
            @choose-filter="currentTab = FilterTab.TYPE"
          >
            {{ $t('filters-modal.type') }}
          </FilterBtn>
          <FilterBtn with-button @choose-filter="currentTab = FilterTab.TAGS">
            {{ $t('filters-modal.tags') }}
          </FilterBtn>
          <FilterBtn
            v-if="props.hub.isLaunchEnabled"
            with-button
            @choose-filter="currentTab = FilterTab.STATUS"
          >
            {{ $t('filters-modal.status') }}
          </FilterBtn>
        </div>

        <div v-if="organization?.isContentCustomizationEnabled">
          <h3 class="mt-7 font-semibold text-secondary px-4">
            {{ $t('filters-modal.customizable-content') }}
          </h3>
          <FilterBtn
            v-model="filtersToSave.customizableOnly"
            class="mt-2"
            with-checkbox
          >
            {{ $t('filters-modal.show-customizable-content') }}
          </FilterBtn>
        </div>
      </div>

      <div v-if="currentTab === FilterTab.SUBCATEGORY">
        <div class="mt-7">
          <h3 class="px-4 font-bold text-secondary">
            {{ $t('filters-modal.subcategories') }}
          </h3>
          <FilterBtn
            class="mt-2"
            :model-value="filtersToApply.subcategoryId === null"
            with-background
            with-checkbox
            @update:model-value="setSubcategory(null)"
          >
            {{ $t('filters-modal.all') }}
          </FilterBtn>
          <FilterBtn
            v-for="(subcategory, index) in currentCategory?.subcategories ?? []"
            :key="`subcategory-${index}`"
            :model-value="filtersToSave.subcategoryId === subcategory.id"
            :with-background="index % 2 !== 0"
            with-checkbox
            @update:model-value="setSubcategory(subcategory)"
          >
            {{ subcategory.name }}
          </FilterBtn>
        </div>
      </div>

      <div v-if="currentTab === FilterTab.TYPE" class="flex-grow">
        <div class="mt-7">
          <FilterBtn
            v-for="(typeOption, index) in typeOptions"
            :key="`type-${index}`"
            :model-value="filtersToSave.type.includes(typeOption.key)"
            :with-background="index % 2 === 0"
            with-checkbox
            @update:model-value="
              filtersToSave.type = getKeyToggledArray(
                filtersToSave.type,
                typeOption.key,
              )
            "
          >
            {{ typeOption.label }}
          </FilterBtn>
        </div>
      </div>

      <div v-if="currentTab === FilterTab.TAGS" class="flex-grow">
        <div class="mt-7">
          <FilterBtn
            v-for="(tag, index) in props.hub.tags"
            :key="`tag-${index}`"
            :model-value="filtersToSave.tagIds.includes(tag.id)"
            :with-background="index % 2 === 0"
            with-checkbox
            @update:model-value="
              filtersToSave.tagIds = getKeyToggledArray(
                filtersToSave.tagIds,
                tag.id,
              )
            "
          >
            {{ tag.name }}
          </FilterBtn>
        </div>
      </div>

      <div v-if="currentTab === FilterTab.STATUS" class="flex-grow">
        <div class="mt-7">
          <FilterBtn
            v-for="(statusOption, index) in LUNCH_METRICS_OPTIONS"
            :key="`tag-${index}`"
            :model-value="filtersToSave.status.includes(statusOption.key)"
            :with-background="index % 2 === 0"
            with-checkbox
            @update:model-value="
              filtersToSave.status = getKeyToggledArray(
                filtersToSave.status,
                statusOption.key,
              )
            "
          >
            {{ statusOption.label }}
          </FilterBtn>
        </div>
      </div>
    </template>

    <div class="px-4">
      <hr />
      <div class="flex justify-between mt-5">
        <Btn
          :size="BtnSize.LG"
          :color="BtnColor.SECONDARY"
          @click="onFilterCancel"
        >
          {{ $t('common.cancel') }}
        </Btn>
        <Btn
          v-if="showConfirmButton"
          :size="BtnSize.LG"
          :variant="BtnVariant.FILLED"
          :color="BtnColor.PRIMARY"
          @click="onFilterApply"
        >
          {{
            currentTab === FilterTab.GENERAL
              ? $t('common.apply')
              : $t('common.add')
          }}
        </Btn>
      </div>
    </div>
    <template #actions="{ closeModal }">
      <div class="px-4 pb-4">
        <hr />
        <div class="flex justify-between mt-5">
          <Btn
            :size="BtnSize.LG"
            :color="BtnColor.SECONDARY"
            @click="onFilterCancel(closeModal)"
          >
            {{ $t('common.cancel') }}
          </Btn>
          <Btn
            v-if="showConfirmButton"
            :size="BtnSize.LG"
            :variant="BtnVariant.FILLED"
            :color="BtnColor.PRIMARY"
            @click="onFilterApply(closeModal)"
          >
            {{
              currentTab === FilterTab.GENERAL
                ? $t('common.apply')
                : $t('common.add')
            }}
          </Btn>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from '#imports';
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import { ContentType, useContentTypeLabelMap } from '~/api/contents';
import { FilterBtn } from '~/pages/_components/hub-details/_components/filters-modal/_components/filter-btn';
import { FilterHeader } from '~/pages/_components/hub-details/_components/filters-modal/_components/filter-header';
import { getKeyToggledArray } from '~/components/inputs/checkbox';
import { ActiveFilters } from '~/pages/_components/hub-details/_components/filters-modal/_components/mobile-filters/_components/active-filters';
import { Nullable } from '~/types/common';
import {
  HubCategoryDto,
  HubDto,
  HubSubcategoryDto,
  HubTagDto,
} from '~/api/hubs';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import { storeToRefs } from 'pinia';
import { useOrganizationStore } from '~/store/organization';
import { HubFilters } from '~/pages/_components/hub-details/_types';
import { USE_LUNCH_METRICS_OPTIONS } from '~/pages/_components/hub-details/_components/filters-modal/_constants';
import {
  Modal,
  ModalPosition,
  ModalShape,
  ModalSize,
} from '~/components/modals/modal';
import { Icon, IconSize } from '~/components/icon';
import FilterSvg from '~/assets/icons/filter.svg?component';

type Props = {
  hub: HubDto;
};

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
}>();

const LUNCH_METRICS_OPTIONS = USE_LUNCH_METRICS_OPTIONS();
const ContentTypeLabelMap = useContentTypeLabelMap();
const filters = defineModel<HubFilters>({ required: true });

const { organization } = storeToRefs(useOrganizationStore());

const i18n = useI18n();

export type HubMobileFilters = Omit<HubFilters, 'search'>;
enum FilterTab {
  GENERAL = 'general',
  SUBCATEGORY = 'subcategory',
  TYPE = 'type',
  TAGS = 'tags',
  STATUS = 'status',
}

const defaultFilters: HubMobileFilters = {
  type: [...filters.value.type],
  tagIds: [...filters.value.tagIds],
  status: [...filters.value.status],
  categoryId: filters.value.categoryId,
  subcategoryId: filters.value.subcategoryId,
  customizableOnly: filters.value.customizableOnly,
};

const currentTab = ref(FilterTab.GENERAL);
const filtersToSave = ref(defaultFilters);
const filtersToApply = ref(defaultFilters);

const currentCategory = computed(() => {
  return (
    props.hub.categories.find(
      (category) => category.id === filtersToApply.value.categoryId,
    ) ?? null
  );
});

const currentSubcategory = computed(() => {
  if (currentCategory.value === null) {
    return null;
  }
  return (
    currentCategory.value.subcategories.find(
      (subcategory) => subcategory.id === filtersToApply.value.subcategoryId,
    ) ?? null
  );
});

const modalTitle = computed<string>(() => {
  if (currentTab.value === FilterTab.GENERAL) {
    return i18n.t('filters-modal.categories-and-filters');
  }
  // TODO add translations
  if (currentTab.value === FilterTab.TYPE) {
    return 'Type';
  }
  if (currentTab.value === FilterTab.TAGS) {
    return 'Tags';
  }
  if (currentTab.value === FilterTab.STATUS) {
    return 'Status';
  }
  return currentCategory.value?.name ?? '';
});

const showConfirmButton = computed<boolean>(() => {
  const mobileFilters = omit(filters.value, ['search']);
  return !isEqual(mobileFilters, filtersToApply.value);
});

const typeOptions = Object.values(ContentType).map((type) => ({
  key: type,
  label: ContentTypeLabelMap[type],
}));

const setSubcategory = (subcategory: Nullable<HubSubcategoryDto>) => {
  filtersToSave.value.subcategoryId = subcategory?.id ?? null;
};

const setCategory = (
  category: Nullable<HubCategoryDto>,
  showSubcategories = false,
) => {
  filtersToApply.value.categoryId = category?.id ?? null;

  if (category === null) {
    setSubcategory(null);
  }

  if (showSubcategories && category !== null) {
    currentTab.value = FilterTab.SUBCATEGORY;
  }
};

const setDefaultCategory = () => {
  setCategory(null);
  setSubcategory(null);
};

const onTypeRemove = (selectedTypeKey: string) => {
  filtersToApply.value.type = filtersToApply.value.type.filter(
    (type) => type !== selectedTypeKey,
  );
};

const onTagRemove = (selectedTagId: number) => {
  filtersToApply.value.tagIds = filtersToApply.value.tagIds.filter(
    (tagId) => tagId !== selectedTagId,
  );
};

const onStatusRemove = (statusKey: string) => {
  filtersToApply.value.status = filtersToApply.value.status.filter(
    (status) => statusKey !== status,
  );
};

const onFilterCancel = (closeModal: () => void) => {
  if (currentTab.value === FilterTab.TAGS) {
    filtersToSave.value.tagIds = [...filters.value.tagIds];
  }
  if (currentTab.value === FilterTab.TYPE) {
    filtersToSave.value.type = [...filters.value.type];
  }

  if (currentTab.value === FilterTab.STATUS) {
    filtersToSave.value.status = [...filters.value.status];
  }

  if (currentTab.value === FilterTab.SUBCATEGORY) {
    filtersToSave.value.subcategoryId = filters.value.subcategoryId;
  }

  if (currentTab.value === FilterTab.GENERAL) {
    closeModal();
    emit('close');
  }

  currentTab.value = FilterTab.GENERAL;
};

const onFilterApply = (closeModal: () => void) => {
  if (currentTab.value === FilterTab.TAGS) {
    filtersToApply.value.tagIds = [...filtersToSave.value.tagIds];
  }
  if (currentTab.value === FilterTab.TYPE) {
    filtersToApply.value.type = [...filtersToSave.value.type];
  }

  if (currentTab.value === FilterTab.SUBCATEGORY) {
    filtersToApply.value.subcategoryId = filtersToSave.value.subcategoryId;
  }

  if (currentTab.value === FilterTab.GENERAL) {
    filters.value.tagIds = [...filtersToApply.value.tagIds];
    filters.value.type = [...filtersToApply.value.type];
    filters.value.status = [...filtersToApply.value.status];
    filters.value.categoryId = filtersToApply.value.categoryId;
    filters.value.subcategoryId = filtersToApply.value.subcategoryId;
    filters.value.customizableOnly = filtersToApply.value.customizableOnly;

    closeModal();
    emit('close');
  }
  currentTab.value = FilterTab.GENERAL;
};

const isFilterApplied = computed(() => {
  return (
    filtersToApply.value.tagIds.length > 0 ||
    filtersToApply.value.type.length > 0 ||
    filtersToApply.value.status.length > 0 ||
    filtersToApply.value.categoryId !== null ||
    filtersToApply.value.subcategoryId !== null ||
    filtersToApply.value.customizableOnly
  );
});

const onFiltersReset = () => {
  filtersToApply.value.tagIds = [];
  filtersToApply.value.type = [];
  filtersToApply.value.status = [];
  filtersToApply.value.categoryId = null;
  filtersToApply.value.subcategoryId = null;
  filtersToApply.value.customizableOnly = false;
};
</script>
